import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type { CreateInvoiceInput } from 'shared';

export function useInvoices(
  params?: Record<string, string>,
  options?: { refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: ['invoices', params],
    queryFn: () => apiClient.get('/invoices', params),
    // Polling — used on the dashboard so the paid state (and the unpaid-deposit
    // banner) updates automatically after a payment without a manual refresh.
    refetchInterval: options?.refetchInterval ?? false,
  });
}

export function useInvoice(id: string) {
  return useQuery({
    queryKey: ['invoice', id],
    queryFn: () => apiClient.get(`/invoices/${id}`),
    enabled: !!id,
  });
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateInvoiceInput) => apiClient.post('/invoices', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useUpdateInvoiceStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiClient.patch(`/invoices/${id}/status`, { status }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', variables.id] });
    },
  });
}

export function usePayInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, embedded = false }: { id: string; embedded?: boolean }) =>
      apiClient.post(`/invoices/${id}/pay`, { embedded }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
    },
  });
}

// Server-verified payment confirmation. After the embedded checkout completes,
// the client calls this so the invoice is marked paid even if the Stripe webhook
// is delayed or unreachable (e.g. local dev). The server checks Stripe directly.
export function useVerifyInvoicePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.post(`/invoices/${id}/verify-payment`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
    },
  });
}
